import type { Furniture, FurniturePart, MaterialId, Vec2 } from "../../types";
import { clamp, part, rect, resetPartIds } from "../geometry";
import {
  buildSinkTrack,
  fixtureCenterOffset,
  sizeSinkFixture,
  type SinkTrack,
} from "../sinkPath";
import {
  bool,
  num,
  resolveParams,
  str,
  type BuildContext,
  type BuildResult,
  type FurnitureTemplate,
} from "./types";

/**
 * Kitchen / bath counters and islands.
 *
 * Frame: origin at the bounding-box center, `+y` = the side you stand on.
 * A straight run fills the bbox. An L or U keeps a standard ~25" slab depth
 * and return width, so `width` × `depth` is the overall box (same convention
 * as sectionals) and the notch is open floor.
 *
 * The cabinet (`base`) sits flush to the walls and stops short of the exposed
 * edges, so the stone top's overhang reads from above. Sinks and the
 * backsplash are drawn on the slab.
 */

const SLAB = 1.25;
const RUN_IN = 25;
const CABINET: { id: MaterialId; tint: string } = { id: "matte", tint: "#e4dfd6" };
const SINK: { id: MaterialId; tint: string } = { id: "metal", tint: "#c5c9d0" };
const FAUCET: { id: MaterialId; tint: string } = { id: "metal", tint: "#7e848e" };

/** Set by the most recent `pushSlab`. See {@link sinkTrackFor}. */
let plannedTrack: SinkTrack | null = null;

interface Host {
  x: number;
  y: number;
  w: number;
  d: number;
}

/** Preferred size, never more than `maxFraction` of `total`, never so big the notch closes. */
function fit(total: number, preferred: number, maxFraction: number): number {
  const cap = Math.max(0.75, total * maxFraction);
  const hi = Math.min(preferred, cap);
  const lo = Math.min(hi, Math.max(0.5, total * 0.12));
  return clamp(preferred, lo, hi);
}

function slabThickness(h: number): number {
  return Math.min(SLAB, Math.max(0.2, h * 0.28));
}

const SINK_AT = {
  id: "sinkAt",
  label: "Sink position",
  type: "int" as const,
  min: -1,
  max: 1000,
  default: -1,
  hidden: true,
};

/** Centerline through `host`, wall on the small-y side, travel left to right. */
function straightTrack(host: Host, kind: string, footprint: Vec2[]): SinkTrack | null {
  const fixture = sizeSinkFixture(host, kind);
  if (!fixture) return null;
  const cy = host.y + fixtureCenterOffset(host.d, fixture.basinD);
  return buildSinkTrack({
    points: [
      { x: host.x, y: cy },
      { x: host.x + host.w, y: cy },
    ],
    auto: { x: host.x + host.w / 2, y: cy },
    fixture,
    footprint,
  });
}

/**
 * Centerline for an L or U. `returns` are the vertical legs in travel order
 * (wall on the left): each is the return's cross-track host and which side
 * the wall is on. The main run is the horizontal leg between them, or before
 * the only return.
 */
function shapedTrack(
  main: Host,
  returns: { host: Host; wall: "left" | "right" }[],
  kind: string,
  footprint: Vec2[],
  ends: { yFront: number },
  preferMain: boolean
): SinkTrack | null {
  const sizing = preferMain ? main : returns[0]?.host;
  if (!sizing) return null;
  const fixture = sizeSinkFixture(sizing, kind);
  if (!fixture) return null;
  const cy = main.y + fixtureCenterOffset(main.d, fixture.basinD);
  const cxOf = (ret: { host: Host; wall: "left" | "right" }) => {
    const off = fixtureCenterOffset(ret.host.w, fixture.basinD);
    return ret.wall === "left" ? ret.host.x + off : ret.host.x + ret.host.w - off;
  };
  const points: Vec2[] = [];
  // Travel with the wall on the left: up a left return, across the main run, down a right return.
  const left = returns.find((r) => r.wall === "left");
  const right = returns.find((r) => r.wall === "right");
  if (left) points.push({ x: cxOf(left), y: ends.yFront }, { x: cxOf(left), y: cy });
  else points.push({ x: main.x, y: cy });
  if (right) points.push({ x: cxOf(right), y: cy }, { x: cxOf(right), y: ends.yFront });
  else points.push({ x: main.x + main.w, y: cy });
  const auto = preferMain
    ? { x: main.x + main.w / 2, y: cy }
    : {
        x: cxOf(returns[0]),
        y: returns[0].host.y + returns[0].host.d / 2,
      };
  return buildSinkTrack({ points, auto, fixture, footprint });
}

function pushSlab(
  parts: FurniturePart[],
  footprint: Vec2[],
  cabinet: Vec2[],
  splashes: Vec2[][],
  track: SinkTrack | null,
  sinkAt: number,
  h: number
) {
  // Remembered so {@link sinkTrackFor} can hand the canvas the same path
  // the parts were just built from. Builds are synchronous.
  plannedTrack = track;
  const slab = slabThickness(h);
  const top0 = h - slab;
  parts.push(part("base", cabinet, 0, top0, { material: CABINET }));
  parts.push(
    part("top", footprint, top0, h, { cornerRadius: footprint.length === 4 ? 0.45 : undefined })
  );
  for (const splash of splashes) {
    parts.push(part("top", splash, h, h + Math.min(4, Math.max(0.4, h * 0.12))));
  }
  if (!track) return;
  const permille = sinkAt < 0 ? track.autoPermille : track.nearestValid(sinkAt);
  const pose = track.poseAt(permille);
  for (const item of track.outlines(pose)) {
    parts.push(
      part("detail", item.basin, h, h + 0.15, {
        cornerRadius: track.cornerRadius,
        material: SINK,
      })
    );
    parts.push(
      part("detail", item.faucet, h, h + 0.4, {
        cornerRadius: 0.35,
        material: FAUCET,
      })
    );
  }
}

export const counterTemplate: FurnitureTemplate = {
  id: "counter",
  label: "Counter",
  frontKind: "none",
  defaultMaterial: "quartz",
  params: [
    {
      id: "layout",
      label: "Layout",
      type: "enum",
      default: "straight",
      options: [
        { value: "straight", label: "Straight" },
        { value: "l-left", label: "L (left return)" },
        { value: "l-right", label: "L (right return)" },
        { value: "u", label: "U" },
      ],
    },
    {
      id: "sink",
      label: "Sink",
      type: "enum",
      default: "single",
      options: [
        { value: "none", label: "None" },
        { value: "single", label: "Single" },
        { value: "double", label: "Double" },
      ],
    },
    { id: "backsplash", label: "Backsplash", type: "bool", default: true },
    SINK_AT,
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const layout = str(params, "layout", "straight");
    const sink = str(params, "sink", "single");
    const sinkAt = num(params, "sinkAt", -1);
    const splashOn = bool(params, "backsplash", true);
    const x0 = -w / 2;
    const y0 = -d / 2;
    const x1 = w / 2;
    const y1 = d / 2;
    const parts: FurniturePart[] = [];

    const shaped = layout === "l-left" || layout === "l-right" || layout === "u";
    if (shaped) {
      const runD = fit(d, RUN_IN, 0.45);
      const retW = fit(w, RUN_IN, layout === "u" ? 0.32 : 0.42);
      const returnLen = d - runD;
      const gap = layout === "u" ? w - 2 * retW : w - retW;
      if (runD >= 2 && retW >= 2 && returnLen >= 2 && gap >= 2) {
        const yMain = y0 + runD;
        const oh = Math.min(1.15, runD * 0.25, retW * 0.25, returnLen * 0.25);
        const lip = splashOn ? Math.min(1.2, runD * 0.12, retW * 0.12) : 0;
        const end = Math.min(0.6, gap * 0.08);

        if (layout === "u") {
          const xL = x0 + retW;
          const xR = x1 - retW;
          const footprint: Vec2[] = [
            { x: x0, y: y0 },
            { x: x1, y: y0 },
            { x: x1, y: y1 },
            { x: xR, y: y1 },
            { x: xR, y: yMain },
            { x: xL, y: yMain },
            { x: xL, y: y1 },
            { x: x0, y: y1 },
          ];
          const cabinet: Vec2[] = [
            { x: x0, y: y0 },
            { x: x1, y: y0 },
            { x: x1, y: y1 - oh },
            { x: xR + oh, y: y1 - oh },
            { x: xR + oh, y: yMain - oh },
            { x: xL - oh, y: yMain - oh },
            { x: xL - oh, y: y1 - oh },
            { x: x0, y: y1 - oh },
          ];
          const splashes: Vec2[][] = lip > 0.15
            ? [rect(x0, y0, w, lip), rect(x0, y0, lip, d), rect(x1 - lip, y0, lip, d)]
            : [];
          const mainHost: Host = {
            x: xL + 1,
            y: y0 + lip + 0.5,
            w: Math.max(0, xR - xL - 2),
            d: Math.max(0, runD - lip - oh - 1.2),
          };
          const leftHost: Host = {
            x: x0 + lip + 0.4,
            y: yMain + 0.8,
            w: Math.max(0, retW - oh - lip - 0.8),
            d: Math.max(0, returnLen - oh - 1.6),
          };
          const rightHost: Host = {
            x: xR + oh + 0.4,
            y: yMain + 0.8,
            w: Math.max(0, retW - oh - lip - 0.8),
            d: Math.max(0, returnLen - oh - 1.6),
          };
          const track = shapedTrack(
            mainHost,
            [
              { host: leftHost, wall: "left" },
              { host: rightHost, wall: "right" },
            ],
            sink,
            footprint,
            { yFront: y1 - oh },
            mainHost.w >= 14 && mainHost.d >= 9
          );
          pushSlab(parts, footprint, cabinet, splashes, track, sinkAt, h);
          return { parts, footprint };
        }

        const right = layout === "l-right";
        const xInner = right ? x1 - retW : x0 + retW;
        const footprint: Vec2[] = right
          ? [
              { x: x0, y: y0 },
              { x: x1, y: y0 },
              { x: x1, y: y1 },
              { x: xInner, y: y1 },
              { x: xInner, y: yMain },
              { x: x0, y: yMain },
            ]
          : [
              { x: x0, y: y0 },
              { x: x1, y: y0 },
              { x: x1, y: yMain },
              { x: xInner, y: yMain },
              { x: xInner, y: y1 },
              { x: x0, y: y1 },
            ];
        const cabinet: Vec2[] = right
          ? [
              { x: x0 + end, y: y0 },
              { x: x1, y: y0 },
              { x: x1, y: y1 - oh },
              { x: xInner + oh, y: y1 - oh },
              { x: xInner + oh, y: yMain - oh },
              { x: x0 + end, y: yMain - oh },
            ]
          : [
              { x: x0, y: y0 },
              { x: x1 - end, y: y0 },
              { x: x1 - end, y: yMain - oh },
              { x: xInner - oh, y: yMain - oh },
              { x: xInner - oh, y: y1 - oh },
              { x: x0, y: y1 - oh },
            ];
        const splashes: Vec2[][] = [];
        if (lip > 0.15) {
          splashes.push(rect(x0, y0, w, lip));
          splashes.push(right ? rect(x1 - lip, y0, lip, d) : rect(x0, y0, lip, d));
        }
        const mainHost: Host = right
          ? {
              x: x0 + end + 1,
              y: y0 + lip + 0.5,
              w: Math.max(0, xInner - x0 - end - 2),
              d: Math.max(0, runD - lip - oh - 1.2),
            }
          : {
              x: xInner + 1,
              y: y0 + lip + 0.5,
              w: Math.max(0, x1 - end - (xInner + 1)),
              d: Math.max(0, runD - lip - oh - 1.2),
            };
        const returnHost: Host = right
          ? {
              x: xInner + oh + 0.4,
              y: yMain + 0.8,
              w: Math.max(0, retW - oh - lip - 0.8),
              d: Math.max(0, returnLen - oh - 1.6),
            }
          : {
              x: x0 + lip + 0.4,
              y: yMain + 0.8,
              w: Math.max(0, retW - oh - lip - 0.8),
              d: Math.max(0, returnLen - oh - 1.6),
            };
        // The sink belongs on the run you stand at. The return is only a
        // fallback when the main span is too narrow (a short leg of an L).
        const mainFits = mainHost.w >= 14 && mainHost.d >= 9;
        const track = shapedTrack(
          mainHost,
          [{ host: returnHost, wall: right ? "right" : "left" }],
          sink,
          footprint,
          { yFront: y1 - oh },
          mainFits
        );
        pushSlab(parts, footprint, cabinet, splashes, track, sinkAt, h);
        return { parts, footprint };
      }
    }

    // Straight run (also the fallback when an L/U can't keep a real notch).
    const oh = Math.min(1.15, d * 0.22);
    const end = Math.min(0.55, w * 0.04);
    const lip = splashOn ? Math.min(1.2, d * 0.08) : 0;
    const footprint = rect(x0, y0, w, d);
    const cabinet = rect(x0 + end, y0, Math.max(1, w - end * 2), Math.max(1, d - oh));
    const splashes = lip > 0.15 ? [rect(x0, y0, w, lip)] : [];
    const host: Host = {
      x: x0 + 1.5,
      y: y0 + lip + 0.5,
      w: Math.max(0, w - 3),
      d: Math.max(0, d - lip - oh - 1.2),
    };
    pushSlab(parts, footprint, cabinet, splashes, straightTrack(host, sink, footprint), sinkAt, h);
    return { parts };
  },
};

export const islandTemplate: FurnitureTemplate = {
  id: "island",
  label: "Island",
  frontKind: "none",
  defaultMaterial: "quartz",
  params: [
    { id: "seating", label: "Seating overhang", type: "bool", default: false },
    {
      id: "sink",
      label: "Sink",
      type: "enum",
      default: "none",
      options: [
        { value: "none", label: "None" },
        { value: "single", label: "Single" },
        { value: "double", label: "Double" },
      ],
    },
    SINK_AT,
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const seating = bool(params, "seating", false);
    const sink = str(params, "sink", "none");
    const sinkAt = num(params, "sinkAt", -1);
    const x0 = -w / 2;
    const y0 = -d / 2;
    const side = Math.min(1.25, w * 0.06, d * 0.06);
    let back = side;
    let front = seating ? Math.min(12, d * 0.42) : side;
    if (front + back > d * 0.8) {
      const scale = (d * 0.65) / (front + back);
      front *= scale;
      back *= scale;
    }
    const cabW = Math.max(1, w - side * 2);
    const cabD = Math.max(1, d - back - front);
    const parts: FurniturePart[] = [];
    const footprint = rect(x0, y0, w, d);
    const cabinet = rect(x0 + side, y0 + back, cabW, cabD);
    const host: Host = {
      x: x0 + side + 1,
      y: y0 + back + 1,
      w: Math.max(0, cabW - 2),
      d: Math.max(0, cabD - 2),
    };
    pushSlab(parts, footprint, cabinet, [], straightTrack(host, sink, footprint), sinkAt, h);
    return { parts };
  },
};

/**
 * The sink path for a counter or island, or null when the piece has no sink.
 * Replays the template build so the drag gesture uses the centerline the
 * basins were drawn on.
 */
export function sinkTrackFor(f: Pick<Furniture, "width" | "depth" | "height" | "model">): SinkTrack | null {
  const model = f.model;
  if (!model || model.kind !== "parametric") return null;
  if (model.template !== "counter" && model.template !== "island") return null;
  const template = model.template === "counter" ? counterTemplate : islandTemplate;
  const params = resolveParams(template, model.params);
  if (str(params, "sink", model.template === "counter" ? "single" : "none") === "none") return null;
  plannedTrack = null;
  template.build({ w: f.width, d: f.depth, h: f.height, params });
  return plannedTrack;
}
